/**
 * useTicketData - Ticket fetching, pagination, and view counts
 *
 * Extracted from useAdminReports.js. Owns:
 * - Report list state and loading flags
 * - GSI-based ticket fetching with infinite scroll (single-org) or
 *   fetch-all-pages (multi-org)
 * - Per-view ticket counts for the filter tabs
 *
 * @param {string} organization - Base organization from route params
 * @param {Object} deps
 * @param {Object} deps.filtersHook - useReportFilters instance (org/HRO/KPI filters)
 * @param {string} deps.ticketView - Current ticket view key (dependency tracking)
 * @param {Object} deps.ticketViewRef - Ref holding the effective view (parent may auto-switch it mid-fetch)
 * @param {boolean} deps.includePreviousReports - Unresponded-only toggle
 * @param {boolean} deps.includeAssigned - "All Open" include-assigned toggle
 * @param {boolean} deps.showOlderTickets - Disable the default 30-day window
 * @param {string} deps.dateRangeStart - Custom range start ('YYYY-MM-DD', empty = unbounded)
 * @param {string} deps.dateRangeEnd - Custom range end ('YYYY-MM-DD', empty = unbounded)
 * @param {Function} deps.onInitialFetch - Called on each fresh (non-loadMore) fetch, before results land
 * @param {Function} deps.handleError - Error handler from useErrorState
 */

import { useState, useCallback, useRef } from 'react';

import Util from '../util/util.js';
import {
  filterByHRO,
  filterByKPI,
  filterUnrespondedOnly,
  extractKPIsAndLocations,
  buildKPIFilterOptions,
  TIME_CONSTRAINED_VIEWS,
  DEFAULT_TIME_CONSTRAINT_MS,
  PAGE_SIZE,
  dateStringToMillis,
} from '../util/reports';
import { t } from '../i18n';

export default function useTicketData(
  organization,
  {
    filtersHook,
    ticketView,
    ticketViewRef,
    includePreviousReports,
    includeAssigned,
    showOlderTickets,
    dateRangeStart,
    dateRangeEnd,
    onInitialFetch,
    handleError,
  }
) {
  // Report data
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);

  // Pagination state for infinite scroll
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Use ref for nextCursor to avoid dependency cycle in fetchTickets
  const nextCursorRef = useRef(null);
  // Cache the minTimestamp from the initial fetch so loadMore uses the same value
  // (recalculating Date.now() on loadMore produces a different timestamp, making the cursor stale)
  const minTimestampRef = useRef(null);
  const maxTimestampRef = useRef(null);
  // Ref-based guard to prevent concurrent loadMore calls (state updates are async)
  const loadingMoreRef = useRef(false);

  // Ticket counts for filter tabs
  const [ticketCounts, setTicketCounts] = useState(null);

  /**
   * Fetch ticket counts for all views
   * Respects time constraint (30 days) unless showOlderTickets is enabled
   * Aggregates counts across multiple organizations if org filter is applied
   */
  const fetchTicketCounts = useCallback(async () => {
    try {
      // Apply time constraint by default (unless user opts to show older)
      const minTimestamp = showOlderTickets ? null : Date.now() - DEFAULT_TIME_CONSTRAINT_MS;

      // Use filtered orgs if available, otherwise use base organization
      const orgsToQuery = filtersHook.OrgFilterValue?.length > 0
        ? filtersHook.OrgFilterValue
        : [organization];

      // Single batched API call — backend accepts comma-separated orgs
      const queryParams = {
        organization: orgsToQuery.join(','),
        ...(minTimestamp && { minTimestamp }),
      };
      const data = await Util.get('Resty', '/items/tickets/counts', { queryParams });
      const counts = data.counts || {};
      setTicketCounts(counts);
      return counts;
    } catch (err) {
      // Silently fail - counts are optional UI enhancement
      console.warn('Failed to fetch ticket counts:', err);
      return null;
    }
  }, [organization, filtersHook.OrgFilterValue, showOlderTickets]);

  /**
   * Fetch tickets using the new GSI-based endpoint
   * Supports infinite scroll - fetches one page at a time for single org queries
   * @param {boolean} isLoadMore - If true, append to existing reports instead of replacing
   */
  const fetchTickets = useCallback(async (isLoadMore = false) => {
    const effectiveView = ticketViewRef.current;

    try {
      if (isLoadMore) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setReportsLoading(true);
        nextCursorRef.current = null;
        setHasMore(false);
      }

      // Determine which organization(s) to query
      const orgsToQuery = filtersHook.OrgFilterValue?.length > 0
        ? filtersHook.OrgFilterValue
        : [organization];

      // Calculate time constraint for applicable views.
      // On loadMore, reuse the timestamp from the initial fetch so the cursor stays valid
      // (recalculating Date.now() shifts the window and can make the cursor return empty pages).
      const isTimeConstrained = TIME_CONSTRAINED_VIEWS.includes(effectiveView);
      let minTimestamp;
      let maxTimestamp;
      if (isLoadMore) {
        minTimestamp = minTimestampRef.current;
        maxTimestamp = maxTimestampRef.current;
      } else {
        // An explicit start date overrides the view's default 30-day window
        minTimestamp = dateStringToMillis(dateRangeStart)
          ?? ((isTimeConstrained && !showOlderTickets) ? Date.now() - DEFAULT_TIME_CONSTRAINT_MS : null);
        maxTimestamp = dateStringToMillis(dateRangeEnd, true);
        minTimestampRef.current = minTimestamp;
        maxTimestampRef.current = maxTimestamp;
      }

      // For single org, support infinite scroll (one page at a time)
      // For multiple orgs, fetch all pages (pagination across multiple sources is complex)
      const singleOrgMode = orgsToQuery.length === 1;

      const allTickets = [];
      let lastCursor = null;
      let moreAvailable = false;

      for (const org of orgsToQuery) {
        let cursor = isLoadMore && singleOrgMode ? nextCursorRef.current : null;
        let continueLoop = true;

        while (continueLoop) {
          const queryParams = {
            view: effectiveView,
            organization: org,
            limit: PAGE_SIZE,
            ...(cursor && { cursor }),
            // For allOpen view: include assigned tickets if toggle is on
            ...(effectiveView === 'allOpen' && { includeAssigned: includeAssigned ? 'true' : 'false' }),
            // Apply time constraint for applicable views
            ...(minTimestamp && { minTimestamp }),
            // Custom date-range upper bound
            ...(maxTimestamp && { maxTimestamp }),
          };

          const data = await Util.get('Resty', '/items/tickets', { queryParams });
          if (data.items && data.items.length > 0) {
            allTickets.push(...data.items);
          }

          // Check pagination info
          if (data.pagination) {
            if (singleOrgMode) {
              // Single org: stop after one page, track cursor for load more
              lastCursor = data.pagination.nextCursor;
              moreAvailable = data.pagination.hasMore;
              continueLoop = false;
            } else {
              // Multiple orgs: fetch all pages
              continueLoop = data.pagination.hasMore;
              cursor = data.pagination.nextCursor;
            }
          } else {
            continueLoop = false;
          }
        }
      }

      // Deduplicate and sort by timestamp (newest first)
      const uniqueItems = [...new Map(allTickets.map(item => [item.uuid, item])).values()];
      let rawReports = uniqueItems.sort((a, b) => b.timestamp - a.timestamp);

      // Extract and build KPI filter options (only on initial load)
      if (!isLoadMore) {
        const { validKPIs, validLocations } = extractKPIsAndLocations(rawReports);
        filtersHook.setKPIFilter(buildKPIFilterOptions(validKPIs, validLocations));
      }

      // Apply HRO/KPI filters client-side
      let filteredReports = rawReports;
      filteredReports = filterByHRO(filteredReports, filtersHook.HROFilterValue);
      filteredReports = filterByKPI(filteredReports, filtersHook.KPIFilterValue);

      if (includePreviousReports) {
        filteredReports = filterUnrespondedOnly(filteredReports);
      }

      // Update pagination state
      if (singleOrgMode) {
        nextCursorRef.current = lastCursor;
        setHasMore(moreAvailable);
      } else {
        nextCursorRef.current = null;
        setHasMore(false);
      }

      // Clear status-flag cache only on initial load
      if (!isLoadMore) {
        await onInitialFetch();
        setReports(filteredReports);
      } else {
        // Append to existing reports, avoiding duplicates
        let addedNewItems = false;
        setReports(prev => {
          const existingUuids = new Set(prev.map(r => r.uuid));
          const newReports = filteredReports.filter(r => !existingUuids.has(r.uuid));
          if (newReports.length === 0) return prev;
          addedNewItems = true;
          return [...prev, ...newReports];
        });

        // If server says hasMore but we got 0 new items after dedup, stop pagination
        // to prevent infinite loop (e.g. stale cursor returning same items)
        if (!addedNewItems && moreAvailable) {
          setHasMore(false);
          nextCursorRef.current = null;
        }
      }
    } catch (err) {
      handleError(err, { userMessage: t('reports:list.loadFailed') });
    }

    setReportsLoading(false);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on filtersHook fields (its object identity changes every render); ticketViewRef is a stable ref
  }, [organization, filtersHook.OrgFilterValue, filtersHook.HROFilterValue, filtersHook.KPIFilterValue, filtersHook.setKPIFilter, includePreviousReports, ticketView, includeAssigned, showOlderTickets, dateRangeStart, dateRangeEnd, onInitialFetch, handleError]);

  /**
   * Load more tickets (for infinite scroll)
   */
  const loadMore = useCallback(() => {
    if (hasMore && !loadingMoreRef.current && !reportsLoading) {
      fetchTickets(true);
    }
  }, [hasMore, reportsLoading, fetchTickets]);

  return {
    reports,
    setReports,
    reportsLoading,
    setReportsLoading,
    ticketCounts,
    loadingMore,
    hasMore,
    loadMore,
    fetchTickets,
    fetchTicketCounts,
  };
}
